import { useEffect, useState } from 'react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from './Button.tsx';
import { Card } from './Card.tsx';

interface Subscription {
  id: string;
  agentId: string;
  calendarId: string;
  calendarSummary: string | null;
  backgroundColor: string | null;
  enabled: boolean;
}

interface Status {
  connected: boolean;
  emailAddress: string | null;
  connectedAt: string | null;
  oauthClientConfigured: boolean;
  callbackUrl: string;
  subscriptions: Subscription[];
}

export function MyCalendarsPanel(): React.ReactElement {
  const [status, setStatus] = useState<Status | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<Status>('/api/profile/google/status');
      setStatus(r);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load calendar status.');
    }
  }

  useEffect(() => {
    void load();
    // Surface OAuth callback result.
    const params = new URLSearchParams(window.location.search);
    const result = params.get('google_calendar');
    if (result === 'connected') {
      setInfo('Google calendar connected. Pick which calendars to include below.');
      params.delete('google_calendar');
      params.delete('msg');
      const search = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));
      void refreshCalendars();
    } else if (result === 'failed') {
      setError(`Google calendar connection failed: ${params.get('msg') ?? 'unknown error'}`);
      params.delete('google_calendar');
      params.delete('msg');
      const search = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (search ? `?${search}` : ''));
    }
  }, []);

  async function refreshCalendars() {
    try {
      const r = await apiFetch<{ subscriptions: Subscription[] }>('/api/profile/google/calendars');
      setStatus((prev) => (prev ? { ...prev, subscriptions: r.subscriptions } : prev));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to refresh calendar list.');
    }
  }

  function connect() {
    window.location.href = '/api/profile/google/connect';
  }

  async function disconnect() {
    if (!window.confirm('Disconnect your Google calendar? "My calendar" view will stop pulling external events.')) return;
    setBusy(true);
    setError(null);
    try {
      await apiFetch('/api/profile/google/disconnect', { method: 'POST' });
      setInfo('Google calendar disconnected.');
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to disconnect.');
    } finally {
      setBusy(false);
    }
  }

  async function toggle(calendarId: string, enabled: boolean) {
    try {
      await apiFetch('/api/profile/google/calendars', {
        method: 'PATCH',
        json: { calendarId, enabled },
      });
      setStatus((prev) =>
        prev
          ? {
              ...prev,
              subscriptions: prev.subscriptions.map((s) =>
                s.calendarId === calendarId ? { ...s, enabled } : s,
              ),
            }
          : prev,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to toggle calendar.');
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-header)]">My Google calendar</h2>
          <p className="text-xs text-[var(--color-muted)]">
            Connect your <strong>own</strong> Google account so events from your personal
            calendars show on the <strong>My calendar</strong> tab. Independent of the team's
            Google account (set up by an admin under Admin → Team Google integration). Read-only
            — we never modify your calendars.
          </p>
        </div>
        {status?.connected ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={connect}>
              Reconnect
            </Button>
            <Button variant="outline" onClick={() => void disconnect()} disabled={busy}>
              Disconnect
            </Button>
          </div>
        ) : (
          status?.oauthClientConfigured && <Button onClick={connect}>Connect Google</Button>
        )}
      </div>

      {info && <p className="mt-3 text-sm text-green-700">{info}</p>}
      {error && <p className="mt-3 text-sm text-[var(--color-danger)]">{error}</p>}

      {!status ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">Loading…</p>
      ) : !status.oauthClientConfigured ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">
          Google OAuth client isn't configured on the server. An admin needs to set
          <code className="mx-1">GOOGLE_OAUTH_CLIENT_ID</code> and
          <code className="mx-1">GOOGLE_OAUTH_CLIENT_SECRET</code>.
        </p>
      ) : !status.connected ? (
        <p className="mt-3 text-sm text-[var(--color-muted)]">
          Not connected yet. Connecting opens a Google sign-in for{' '}
          <strong>your</strong> account (not the org's ticketing inbox) — pick whichever Google
          account holds the calendars you want to see in T3.
        </p>
      ) : (
        <>
          <p className="mt-3 text-xs text-[var(--color-muted)]">
            Connected as <strong>{status.emailAddress}</strong>
            {status.connectedAt && ` · since ${new Date(status.connectedAt).toLocaleDateString()}`}
          </p>
          {status.subscriptions.length === 0 ? (
            <p className="mt-3 text-sm text-[var(--color-muted)]">
              No calendars found. Try{' '}
              <button
                type="button"
                className="text-[var(--color-t3-blue)] hover:underline"
                onClick={() => void refreshCalendars()}
              >
                refreshing
              </button>
              .
            </p>
          ) : (
            <ul className="mt-3 space-y-1">
              {status.subscriptions.map((s) => (
                <li
                  key={s.calendarId}
                  className="flex items-center gap-2 rounded-[var(--radius-button)] px-2 py-1.5 hover:bg-[var(--color-surface)]"
                >
                  <input
                    type="checkbox"
                    checked={s.enabled}
                    onChange={(e) => void toggle(s.calendarId, e.target.checked)}
                    className="h-4 w-4 cursor-pointer"
                  />
                  {s.backgroundColor && (
                    <span
                      className="inline-block h-3 w-3 rounded-sm"
                      style={{ background: s.backgroundColor }}
                    />
                  )}
                  <span className="flex-1 text-sm">{s.calendarSummary ?? s.calendarId}</span>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            onClick={() => void refreshCalendars()}
            className="mt-2 text-xs text-[var(--color-muted)] hover:text-[var(--color-header)] hover:underline"
          >
            Refresh calendar list
          </button>
        </>
      )}
    </Card>
  );
}
